import { useState, useMemo } from 'react';
import { useCompare } from '../../../context/CompareContext';
import { allTools } from '../../../mocks/tools';
import { TOOL_PRICING } from '../../../mocks/tool-pricing';

const CATEGORIES = [
  'All',
  'AI Productivity Tools',
  'AI Writing Tools',
  'AI Image Generators',
  'AI Video Tools',
  'AI Voice Tools',
  'AI Coding Tools',
  'AI Design Tools',
  'AI Automation Tools',
  'AI Website Builders',
  'AI Marketing Tools',
];

const PAGE_SIZE = 24;

function ToolLogo({ logo, name }: { logo: string; name: string }) {
  const [err, setErr] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!logo || err) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold bg-teal-600 rounded-xl">
        {name.charAt(0)}
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold bg-teal-600 rounded-xl">
          {name.charAt(0)}
        </div>
      )}
      <img
        src={logo}
        alt={name}
        className={`w-full h-full object-contain p-1.5 ${loaded ? 'block' : 'hidden'}`}
        onLoad={() => setLoaded(true)}
        onError={() => { setErr(true); setLoaded(false); }}
      />
    </>
  );
}

function ToolCard({ tool }: { tool: typeof allTools[0] }) {
  const { toggleTool, isInCompare, isFull } = useCompare();
  const selected = isInCompare(tool.slug);
  const pricing = TOOL_PRICING[tool.slug];
  const disabled = isFull && !selected;

  return (
    <div
      className={`relative flex flex-col gap-2 p-3 rounded-xl border transition-all duration-200 cursor-pointer group ${
        selected
          ? 'bg-teal-500/10 border-teal-500/50 shadow-[0_0_0_1px_rgba(20,184,166,0.3)]'
          : disabled
          ? 'bg-gray-900/30 border-gray-800/40 opacity-50'
          : 'bg-gray-900/40 border-gray-800/50 hover:border-gray-700 hover:bg-gray-900/60'
      }`}
      onClick={() => !disabled && toggleTool(tool.slug)}
    >
      {/* Badge */}
      {tool.badge && (
        <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
          tool.badge === 'featured' ? 'bg-amber-500/20 text-amber-300' :
          tool.badge === 'trending' ? 'bg-rose-500/20 text-rose-300' :
          'bg-emerald-500/20 text-emerald-300'
        }`}>
          {tool.badge}
        </div>
      )}

      {/* Logo */}
      <div className="w-10 h-10 rounded-xl bg-gray-800 overflow-hidden shrink-0 self-center mt-1">
        <ToolLogo logo={tool.logo} name={tool.name} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 text-center">
        <p className="text-white text-xs font-semibold leading-tight line-clamp-2">{tool.name}</p>
        <p className="text-gray-500 text-[10px] mt-0.5 leading-tight line-clamp-1">{tool.category.replace('AI ', '')}</p>
        {pricing && (
          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${
            pricing === 'Free' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            pricing === 'Freemium' ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' :
            'bg-orange-500/10 text-orange-400 border-orange-500/20'
          }`}>
            {pricing}
          </span>
        )}
      </div>

      {/* Add/Remove button */}
      <button
        onClick={(e) => { e.stopPropagation(); if (!disabled) toggleTool(tool.slug); }}
        className={`flex items-center justify-center gap-1 w-full py-1.5 rounded-lg text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
          selected
            ? 'bg-teal-500/20 text-teal-300 hover:bg-rose-500/20 hover:text-rose-300'
            : disabled
            ? 'bg-gray-800/40 text-gray-600 cursor-not-allowed'
            : 'bg-gray-800/60 text-gray-400 hover:bg-teal-500/20 hover:text-teal-300'
        }`}
        disabled={disabled}
      >
        {selected ? (
          <>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-line text-xs"></i></span>
            Added
          </>
        ) : disabled ? (
          <>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-lock-line text-xs"></i></span>
            Max 3
          </>
        ) : (
          <>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line text-xs"></i></span>
            Compare
          </>
        )}
      </button>
    </div>
  );
}

interface ToolPickerProps {
  onCompare: () => void;
}

export default function ToolPicker({ onCompare }: ToolPickerProps) {
  const { compareSlugs, count, removeTool, clearAll } = useCompare();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    return allTools.filter(t => {
      const matchCat = activeCategory === 'All' || t.category === activeCategory;
      const q = search.toLowerCase();
      const matchSearch = !q || t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [search, activeCategory]);

  const displayed = showAll ? filtered : filtered.slice(0, PAGE_SIZE);

  const selectedTools = compareSlugs.map(s => allTools.find(t => t.slug === s)).filter(Boolean);

  return (
    <div className="space-y-5">

      {/* Selected tools tray */}
      <div className="bg-gray-900/60 border border-gray-800/60 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-scales-3-line text-teal-400 text-sm"></i>
            </div>
            <span className="text-white text-sm font-semibold">Selected for comparison</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              count === 0 ? 'bg-gray-800 text-gray-500' :
              count >= 2 ? 'bg-teal-500/20 text-teal-300 border border-teal-500/25' :
              'bg-amber-500/20 text-amber-300 border border-amber-500/25'
            }`}>
              {count} / 3
            </span>
          </div>
          {count > 0 && (
            <button
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-rose-400 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-delete-bin-line"></i>
              Clear all
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Filled slots */}
          {selectedTools.map(tool => tool && (
            <div key={tool.slug} className="flex items-center gap-2 bg-teal-500/10 border border-teal-500/25 rounded-xl px-3 py-2">
              <div className="w-6 h-6 rounded-lg overflow-hidden bg-gray-800 shrink-0">
                <ToolLogo logo={tool.logo} name={tool.name} />
              </div>
              <span className="text-white text-xs font-semibold whitespace-nowrap">{tool.name}</span>
              <button
                onClick={() => removeTool(tool.slug)}
                className="w-4 h-4 flex items-center justify-center text-teal-400/60 hover:text-rose-400 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}

          {/* Empty slots */}
          {Array.from({ length: Math.max(0, 2 - count) }).map((_, i) => (
            <div key={`empty-${i}`} className="flex items-center gap-2 border border-gray-700/30 border-dashed rounded-xl px-3 py-2">
              <div className="w-6 h-6 rounded-lg bg-gray-800/50 border border-gray-700/30 border-dashed flex items-center justify-center shrink-0">
                <i className="ri-add-line text-gray-600 text-xs"></i>
              </div>
              <span className="text-gray-600 text-xs whitespace-nowrap">Select a tool</span>
            </div>
          ))}

          {/* Compare button */}
          <button
            onClick={onCompare}
            disabled={count < 2}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer ml-auto ${
              count >= 2
                ? 'bg-teal-500 hover:bg-teal-400 text-white shadow-lg shadow-teal-500/25 hover:scale-105 active:scale-95'
                : 'bg-gray-800 text-gray-600 cursor-not-allowed border border-gray-700/40'
            }`}
          >
            {count < 2 ? (
              <>
                <i className="ri-information-line text-xs"></i>
                Need {2 - count} more tool{2 - count !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                <i className="ri-bar-chart-grouped-line"></i>
                Compare {count} Tools
                <i className="ri-arrow-down-line text-xs"></i>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Search + category filter */}
      <div className="space-y-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setShowAll(false); }}
            placeholder="Search tools by name or category…"
            className="w-full pl-10 pr-4 py-2.5 bg-gray-900/60 border border-gray-700/50 rounded-xl text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all cursor-text"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer w-4 h-4 flex items-center justify-center"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        {/* Category pills - scrollable */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setShowAll(false); }}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeCategory === cat
                  ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/20'
                  : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/60 hover:text-gray-200 border border-gray-700/40'
              }`}
            >
              {cat === 'All' ? `All (${allTools.length})` : cat.replace('AI ', '')}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-xs">
          {filtered.length === 0
            ? 'No tools found'
            : `Showing ${Math.min(displayed.length, filtered.length)} of ${filtered.length} tools`}
        </p>
        {search && (
          <button onClick={() => setSearch('')} className="text-teal-400 text-xs hover:text-teal-300 cursor-pointer whitespace-nowrap">
            Clear search
          </button>
        )}
      </div>

      {/* Tool grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-800/50 rounded-2xl mb-4">
            <i className="ri-search-line text-3xl text-gray-600"></i>
          </div>
          <p className="text-gray-400 text-sm font-medium">No tools found for &quot;{search}&quot;</p>
          <button onClick={() => { setSearch(''); setActiveCategory('All'); }} className="mt-3 text-teal-400 text-sm hover:text-teal-300 cursor-pointer">
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {displayed.map(tool => (
              <ToolCard key={tool.slug} tool={tool} />
            ))}
          </div>

          {!showAll && filtered.length > PAGE_SIZE && (
            <div className="text-center pt-2">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-700/50 text-gray-300 hover:text-white text-sm font-medium rounded-xl transition-all cursor-pointer"
              >
                <i className="ri-arrow-down-line"></i>
                Show {filtered.length - PAGE_SIZE} more tools
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
